import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import styled from '@emotion/styled'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { colors, layering } from '@mobi/component-library/Theme/Common'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { colors as brandColors } from '@mobi/settings/colors'
import { AppRoutes } from '@core/AppRoutes'
import { closeModal } from '@core/Components/Modal'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { setBetSlipIsOpen } from '@core/Areas/AppHeader/Store'
import { postShareToBetLoop } from '@core/Areas/BetLoop/helpers/api'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { useIsBetLoopEntitled } from '@core/Areas/BetLoop/hooks/useIsBetLoopEntitled'
import { handleApiErrorNotification } from '@core/Areas/BetLoop/helpers/handleApiErrorNotification'
import type { BetSlipShare, QuickbetShare, TicketShare } from '@core/Areas/ShareBet/types'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import { FirstPostConfirmation } from '@core/Areas/BetLoop/Components/Common/FirstPostConfirmation'
import { shouldAllowShareInBetLoop } from '@core/Areas/BetLoop/helpers/shouldAllowShareInBetLoop'
import { ProfileAvatar } from '@core/Areas/BetLoop/Components/Common/ProfileAvatar'
import { trackBetLoopShareBetBlocked } from '@core/Areas/BetLoop/helpers/analytics'
import { navigateToMyProfileWithViewBet } from '@core/Areas/BetLoop/helpers/navigation'
import { isBetSlipShareData } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/typeGuards'
import { trackShareBetError, extractShareContext } from '@core/Areas/ShareBet/helpers/analytics'
import { isBetLoopProblemResponse } from '@core/Areas/BetLoop/helpers/api/typeGuards'

const enum LocalConstants {
  ShareToBetLoopSuccessToastId = 'share-to-bet-loop-success',
}

export const BetLoopShareButton: React.FC<{
  data: TicketShare | BetSlipShare | QuickbetShare
  dismissTray: () => void
}> = ({ data, dismissTray }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isFirstPostModalOpen, setFirstPostModalOpen] = useState(false)

  const isEntitled = useIsBetLoopEntitled()
  const { data: profileData, isLoading: isLoadingProfile } = useQuery(betLoopPrincipleProfileQuery)

  const { mutate } = useMutation({
    mutationFn: postShareToBetLoop,
    onSuccess: ({ postId }) => {
      const onToastViewButtonClick = () => {
        dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
        dispatch(setBetSlipIsOpen(false))
        navigateToMyProfileWithViewBet({ postId })
        removeToast({ id: LocalConstants.ShareToBetLoopSuccessToastId })
      }
      const toastOptions: Parameters<typeof addToast>[0] = {
        theme: 'dark',
        icon: 'success',
        title: 'Your bet was shared to Bet Loop',
        id: LocalConstants.ShareToBetLoopSuccessToastId,
        button: { text: 'View', onClick: onToastViewButtonClick },
      }
      isBetSlipShareData(data) && data.share.toastEmitter
        ? data.share.toastEmitter.emit('addToast', toastOptions)
        : addToast(toastOptions)

      queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
      trackShareBetClick({ data, isCostShared: false, destination: 'Bet Loop' })
    },
    onError: async err => {
      const { location, betType } = extractShareContext(data)
      const errorMessage = isBetLoopProblemResponse(err)
        ? err.detail || err.title || 'Unknown error'
        : 'Unknown error'
      trackShareBetError({ message: errorMessage, destination: 'Bet Loop', location, betType })
      handleApiErrorNotification(err)
    },
  })

  const postToBetLoop = () => {
    if (profileData) {
      mutate({ ticketSequence: data.share.ticketSequence as number })
    } else {
      dispatch(setBetSlipIsOpen(false))
      navigate(AppRoutes.BetLoop)
    }
  }

  const handleShareToBetLoopClick = () => {
    if (profileData?.shareCount === 0) {
      setFirstPostModalOpen(true)
    } else {
      dismissTray()
      postToBetLoop()
    }
  }

  const handleFirstPostConfirm = () => {
    setFirstPostModalOpen(false)
    dismissTray()
    postToBetLoop()
  }

  const isShareAllowed = shouldAllowShareInBetLoop(data)

  const handleBlockShareFromBetLoop = () => {
    addToast({
      id: 'addto-sharehub-blocked',
      isDismissable: true,
      title: !isEntitled
        ? 'You don’t currently have access to Bet Loop'
        : 'Your bet cannot be shared to Bet Loop',
      icon: 'warning',
      theme: 'dark',
    })
    trackBetLoopShareBetBlocked(data, isEntitled)
  }

  return (
    <>
      {isShareAllowed && isEntitled ? (
        <button onClick={handleShareToBetLoopClick} disabled={isLoadingProfile} type='button'>
          {profileData ? (
            <ProfileAvatar avatar={profileData.avatar} color={profileData.color} size='sm' />
          ) : (
            <SvgLazy name='BetLoopHeaderLogo' width='7rem' color={brandColors.brand.primary} />
          )}
          Share to Bet Loop
        </button>
      ) : (
        <BlockedButtonStyled onClick={handleBlockShareFromBetLoop} type='button'>
          {profileData ? (
            <div>
              <span>
                <ProfileAvatar avatar={profileData.avatar} color={profileData.color} size='sm' />
              </span>
            </div>
          ) : (
            <SvgLazy name='BetLoopHeaderLogo' width='7rem' color={colors.neutral[500]} />
          )}
          Share to Bet Loop
        </BlockedButtonStyled>
      )}

      {createPortal(
        <FirstPostConfirmation
          isOpen={isFirstPostModalOpen}
          onConfirm={handleFirstPostConfirm}
          onCancel={() => setFirstPostModalOpen(false)}
          zIndex={layering.overlayHigh + 1}
        />,
        document.body
      )}
    </>
  )
}

// ======
// Styles
// ======

const BlockedButtonStyled = styled.button({
  '&[type="button"]': {
    backgroundColor: colors.neutral[200],
    borderColor: colors.neutral[300],
    color: colors.neutral[500],

    ':active': {
      scale: '1 !important',
      backgroundColor: colors.neutral[200],
      borderColor: colors.neutral[300],
      color: colors.neutral[500],
      opacity: 1,
    },

    '> div': {
      position: 'relative',

      '> span': {
        width: '100%',
        height: '100%',
        background: 'black',
        opacity: '50%',
        zIndex: 10,
        position: 'absolute',
      },
    },
  },
})
